import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import type { SettingsResponse, SetupWelcomeResponse } from "../../shared/types";
import { useAuth } from "../auth/AuthContext";
import { useLayout } from "../components/season/layout";
import { BrandGlyph, BrandWord } from "../components/BrandMark";
import { Button } from "../components/ui/Button";
import { cx } from "../components/ui/cx";
import { Face, Ring } from "../components/ui/Face";
import { Icon, type IconName } from "../components/ui/Icon";
import { Pill } from "../components/ui/Pill";
import { useToast } from "../components/ui/Toast";
import { api } from "../lib/api";
import "../styles/setup.css";
import { AddressStep } from "./AddressStep";
import { EmailStep, RecordsStep, TestStep } from "./EmailSteps";
import { FromFlashsuite, LookPickers, usePreviewWho } from "./parts";
import { BrowserFrame, PreviewFit, PreviewPanel, RoomPreview, SignupPreview, usePreviewRoom } from "./Previews";
import { SETUP_STEPS, setupCount, useSetup } from "./SetupContext";
import { type ReadyAct, type ReadyItem, readySummary } from "./ready";
import { SeasonSetup } from "./SeasonSetup";
import { TrackingStep } from "./TrackingStep";
import { useLookChoice } from "./useLookChoice";
import { type Run, useSettingsData } from "./useSettingsData";

export interface StepProps {
  data: SettingsResponse;
  run: Run;
  setData: (d: SettingsResponse) => void;
}

/** One question per screen: the eyebrow, the question, the answers, and the buttons along the bottom. */
export function Sq({ eyebrow, title, children, foot }: { eyebrow: ReactNode; title: ReactNode; children: ReactNode; foot: ReactNode }) {
  return (
    <div className="sq">
      <div className="eyebrow wine">{eyebrow}</div>
      <h1>{title}</h1>
      <div className="qs">{children}</div>
      <div className="sfoot">{foot}</div>
    </div>
  );
}

/** How far into the email step: the address, the records, the test. */
export function MiniSteps({ n }: { n: 1 | 2 | 3 }) {
  return (
    <span className="mini-steps" aria-label={`Part ${n} of 3`}>
      <i className={n >= 1 ? "on" : ""} />
      <i className={n >= 2 ? "on" : ""} />
      <i className={n >= 3 ? "on" : ""} />
    </span>
  );
}

export function BackButton({ to }: { to: string }) {
  const navigate = useNavigate();
  return (
    <Button size="lg" icon="left" onClick={() => navigate(to)}>
      Back
    </Button>
  );
}

type StepKey = (typeof SETUP_STEPS)[number]["key"];

function stepOf(pathname: string): StepKey | null {
  const first = pathname.split("/").filter(Boolean)[1] ?? "";
  return SETUP_STEPS.find((s) => s.key === first)?.key ?? null;
}

/** Leaving setup for now: setup counts as seen, and the top bar's pill says how far it got. */
export function useSkipSetup(): () => Promise<void> {
  const navigate = useNavigate();
  const toast = useToast();
  const { set } = useSetup();
  return async () => {
    try {
      const s = await api.settings.markSetup({ seen: true });
      set(s);
      navigate("/");
      window.setTimeout(() => toast({ text: `Setup: ${setupCount(s)} of 4 done`, icon: "clock" }), 300);
    } catch {
      navigate("/");
    }
  };
}

/**
 * First-run setup: a full screen of its own, outside the app's shell, one question at a time: the look, the email,
 * the web address and tracking. A computer draws the wide stage beside each question; a phone and an iPad draw Season's.
 */
export function SetupView() {
  const layout = useLayout();
  const { data: read, failed, run, setData } = useSettingsData();
  const { state } = useSetup();
  // Why the shared state over the settings read: a step marked done on its own (the address, tracking, seen)
  // changes setup without a new settings read, and every screen here must show it.
  const data = read && state ? { ...read, setup: state } : read;
  const skip = useSkipSetup();
  return layout === "computer" ? (
    <ComputerSetup data={data} failed={failed} run={run} setData={setData} skip={skip} />
  ) : (
    <SeasonSetup layout={layout} data={data} failed={failed} run={run} setData={setData} skip={skip} />
  );
}

interface SetupScreenProps {
  data: SettingsResponse | null;
  failed: boolean;
  run: Run;
  setData: (d: SettingsResponse) => void;
  skip: () => Promise<void>;
}

function ComputerSetup({ data, failed, run, setData, skip }: SetupScreenProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const current = stepOf(pathname);
  const done = pathname.endsWith("/ready");
  const setup = data?.setup;
  return (
    <div className="setup">
      <div className="stop">
        <Link to="/" className="brand" aria-label="Flashroom home">
          <BrandGlyph size={30} />
          <BrandWord />
        </Link>
        <nav className="dots" aria-label="Setup steps">
          {SETUP_STEPS.map((s, i) => {
            const isDone = !!setup?.[s.key];
            return (
              <span key={s.key} style={{ display: "contents" }}>
                {i ? <i className={cx("dline", setup?.[SETUP_STEPS[i - 1].key] && "done")} /> : null}
                <button
                  type="button"
                  className={cx("dotx", isDone && "done", current === s.key && "on")}
                  title={s.label}
                  aria-current={current === s.key ? "step" : undefined}
                  onClick={() => navigate(s.path)}
                >
                  <span className="d">{isDone ? <Icon name="check" /> : i + 1}</span>
                  <span className="lb">{s.label}</span>
                </button>
              </span>
            );
          })}
        </nav>
        <div className="row g8" style={{ justifySelf: "end" }}>
          {done ? null : (
            <Button variant="q" onClick={() => void skip()}>
              Skip for now
            </Button>
          )}
        </div>
      </div>
      {!data ? (
        <div className="sbody center">
          <p className="muted">{failed ? "We couldn’t load your settings. Refresh to try again." : "Loading…"}</p>
        </div>
      ) : (
        <Routes>
          <Route index element={<Welcome data={data} />} />
          <Route path="look" element={<LookStep data={data} run={run} setData={setData} />} />
          <Route path="email" element={<EmailStep data={data} run={run} setData={setData} />} />
          <Route path="email/records" element={<RecordsStep key={data.sending.domain ?? ""} data={data} run={run} setData={setData} />} />
          <Route path="email/test" element={<TestStep data={data} run={run} setData={setData} />} />
          <Route path="address" element={<AddressStep data={data} run={run} setData={setData} />} />
          <Route path="tracking" element={<TrackingStep data={data} run={run} setData={setData} />} />
          <Route path="ready" element={<Ready data={data} />} />
          <Route path="*" element={<Navigate to="/setup" replace />} />
        </Routes>
      )}
    </div>
  );
}

// --- Welcome ---

/** Who the welcome's preview shows, read once: nobody until it answers, and nobody for a brand-new business. */
function useWelcomePeople(): SetupWelcomeResponse | null {
  const [people, setPeople] = useState<SetupWelcomeResponse | null>(null);
  useEffect(() => {
    let live = true;
    api.settings
      .welcome()
      .then((r) => live && setPeople(r))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return people;
}

function Welcome({ data }: { data: SettingsResponse }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const room = usePreviewRoom();
  const who = usePreviewWho(data, room);
  const people = useWelcomePeople();
  const registered = people?.registered;
  const watching = people?.watching;
  const items: Array<[IconName, string, string]> = [
    ["palette", "Your look", ""],
    ["mail", "Your email", ""],
    ["globe", "Your web address", ""],
    ["target", "Tracking", "Optional"],
  ];
  return (
    <div className="welcome-hero">
      <div>
        <div className="eyebrow brass">Welcome to Flashroom</div>
        <h1 className="mt12">Hi {user?.first_name || "there"}. Let’s get Flashroom ready for your people.</h1>
        <div className="wlist">
          {items.map(([icon, t, m]) => (
            <div key={t} className="wli">
              <div className="n">
                <Icon name={icon} />
              </div>
              <div>
                <div className="t">{t}</div>
              </div>
              <div className="tm">{m}</div>
            </div>
          ))}
        </div>
        <div className="row g16 mt24">
          <Button variant="p" size="xl" after="right" onClick={() => navigate("/setup/look")}>
            Let’s start
          </Button>
        </div>
      </div>
      <div className="collage" aria-hidden="true">
        <div className="c1">
          <PreviewFit>
            <BrowserFrame url={`${who.address}/${room?.slug ?? "your-room"}`}>
              <SignupPreview colours={data.brand} who={who} />
            </BrowserFrame>
          </PreviewFit>
        </div>
        <div className="c2">
          <PreviewFit>
            <RoomPreview colours={data.brand} who={who} dark />
          </PreviewFit>
        </div>
        {registered ? (
          <div className="bubblec" style={{ left: -18, top: 0 }}>
            <Face name={registered.name} photo={registered.avatar_url} size={34} />
            <div>
              <b>{registered.name}</b> registered
              <div className="muted xs">{registered.webinar_title}</div>
            </div>
          </div>
        ) : null}
        {watching ? (
          <div className="bubblec" style={{ right: 24, top: 300 }}>
            <Ring name={watching.name} photo={watching.avatar_url} size={30} percent={watching.of_minutes ? (watching.minute / watching.of_minutes) * 100 : 0} live />
            <div>
              <b>{watching.name.split(" ")[0]}</b> is watching
              <div className="muted xs">{watching.minute} min in</div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// --- 1. Your look ---

function LookStep({ data, run }: StepProps) {
  const navigate = useNavigate();
  const room = usePreviewRoom();
  const who = usePreviewWho(data, room);
  const { colours, mode, saving, save, pick } = useLookChoice(data, run);
  const next = async () => {
    if (await save()) navigate("/setup/email");
  };
  return (
    <div className="sbody">
      <Sq
        eyebrow="Step 1 of 4 · Your look"
        title="How should your pages look?"
        foot={
          <>
            <BackButton to="/setup" />
            <span className="sp" />
            <Button variant="p" size="lg" after="right" disabled={saving} onClick={() => void next()}>
              Looks good, continue
            </Button>
          </>
        }
      >
        <FromFlashsuite business={who.business} logoUrl={data.brand.logo_url} />
        <LookPickers mode={mode} colours={colours} onPick={pick} />
      </Sq>
      <div className="sstage">
        <PreviewPanel colours={colours} who={who} />
      </div>
    </div>
  );
}

// --- Ready: every row reads the same state Settings reads ---

/** Setup has been looked at once the ready screen shows. */
export function useSetupSeen(seen: boolean): void {
  const { set } = useSetup();
  useEffect(() => {
    if (seen) return;
    api.settings
      .markSetup({ seen: true })
      .then(set)
      .catch(() => undefined);
  }, [seen, set]);
}

function Ready({ data }: { data: SettingsResponse }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const n = setupCount(data.setup);
  const first = user?.first_name || "there";
  useSetupSeen(data.setup.seen);
  const rows = readySummary(data, user?.workspace_name ?? "");

  return (
    <div className="sbody center">
      <div className="ready">
        <div className="bigck">
          <Icon name="check" />
        </div>
        <h1 className="mt24" style={{ fontSize: 40, fontWeight: 800, letterSpacing: "-.035em" } as CSSProperties}>
          {n === 4 ? `You’re ready, ${first}.` : `Nearly there, ${first}.`}
        </h1>
        {n === 4 ? null : (
          <p className="mt8" style={{ fontSize: 16, color: "var(--ink-2)" }}>
            {n} of 4 done
          </p>
        )}
        <div className="rlist">
          {rows.map((r) => (
            <ReadyRow key={r.key} icon={READY_ICON[r.key]} title={r.title} sub={r.sub} skipped={!r.done} action={<ReadyAction act={r.act} />} />
          ))}
        </div>
        <div className="row g12 mt32 wrap" style={{ justifyContent: "center" }}>
          <Button variant="p" size="xl" icon="plus" onClick={() => navigate("/rooms/new")}>
            Make your first room
          </Button>
          <Button size="xl" onClick={() => navigate("/")}>
            Go to Home
          </Button>
        </div>
      </div>
    </div>
  );
}

export const READY_ICON: Record<ReadyItem["key"], IconName> = { look: "palette", email: "mail", address: "globe", tracking: "target" };

function ReadyAction({ act }: { act: ReadyAct }) {
  const navigate = useNavigate();
  if (act.kind === "copy") return <CopyButton value={act.value} />;
  if (act.kind === "pill") {
    return (
      <Pill tone={act.tone} dot>
        {act.text}
      </Pill>
    );
  }
  return (
    <Button size="s" variant={act.primary ? "p" : undefined} onClick={() => navigate(act.to)}>
      {act.label}
    </Button>
  );
}

function ReadyRow({ icon, title, sub, action, skipped = false }: { icon: IconName; title: string; sub?: string; action: ReactNode; skipped?: boolean }) {
  return (
    <div className="rli">
      <div className={cx("ic", skipped && "skip")}>
        <Icon name={skipped ? icon : "check"} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="b7">{title}</div>
        {sub ? <div className="small muted">{sub}</div> : null}
      </div>
      {action}
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const toast = useToast();
  return (
    <Button
      size="s"
      icon="copy"
      onClick={() =>
        void navigator.clipboard
          ?.writeText(value)
          .then(() => toast({ text: "Copied", icon: "copy" }))
          .catch(() => undefined)
      }
    >
      Copy
    </Button>
  );
}

